import { defineComponent as _defineComponent } from 'vue';
import { ref, computed, watch, onMounted } from 'vue';
import debounce from 'lodash/debounce';
import { BOverlay, BFormRadioGroup, BFormGroup, BFormInput, BButton, BAlert } from 'bootstrap-vue';
import UnitSelector from 'public/components/UnitSelector.vue';
import SizeInput from 'public/components/SizeInput.vue';
import StandardSizeSelector from 'public/components/StandardSizeSelector.vue';
import UpsellSheetProduct from 'public/components/UpsellSheetProduct.vue';
import ProductDiscontinued from 'public/components/materials/ProductDiscontinued.vue';
import DiscountsBreakdown from 'public/components/DiscountsBreakdown.vue';
import { addToCart } from 'public/modules/cart';
import { validateSheet } from 'public/modules/validation';
import { sheetDelivery } from 'public/modules/delivery';
import { checkSheetStock } from 'public/modules/stock';
import { getSheetPrice } from 'public/modules/pricing';
import { getVatMode, isAdmin } from 'public/modules/helpers';
import { useAxios } from 'composables/useAxios';
export default /*#__PURE__*/ _defineComponent({
    __name: 'Sheet',
    props: {
        productSlug: null
    },
    setup(__props) {
        const props = __props;
        const urlParams = new URLSearchParams(window.location.search);
        const axios = useAxios();
        const loading = ref(true);
        const loadingPrice = ref(true);
        const loadingValidation = ref(true);
        const loadingStock = ref(true);
        const isAddingToCart = ref(false);
        const isAdminUser = ref(false);
        const vatMode = ref('inc');
        const product = ref(null);
        const size = ref({
            width: parseInt(urlParams.get('widthMM') || '210'),
            length: parseInt(urlParams.get('lengthMM') || '297'),
            type: !urlParams.get('type') ? 'a' : undefined,
            name: !urlParams.get('name') ? 'A4' : undefined
        });
        const quantity = ref(1);
        const isInStock = ref(true);
        const validation = ref({ problems: [], warnings: [], isValid: true });
        const delivery = ref({ problems: [], warnings: [], isValid: true });
        const sizeType = ref(urlParams.get('type') ? 'bespoke' : 'standard');
        const sizeTypeOptions = [
            { text: 'Custom Size', value: 'bespoke' },
            { text: 'Standard Size', value: 'standard' }
        ];
        const displayUnit = ref('mm');
        const pricing = ref({
            base_unit_price: '0.00',
            ex_vat_base_unit_price: '0.00',
            unit_price: '0.00',
            total_price: '0.00',
            unit_discount: '0.00',
            total_discount: '0.00'
        });
        const isValidQuantity = computed(() => quantity.value > 0);
        const isAddToCartDisabled = computed(() => (((!validation.value.isValid || !isInStock.value) && !isAdminUser.value) || !isValidQuantity.value || isAddingToCart.value));
        const buyButtonVariant = computed(() => validation.value.problems.length > 0 || !isValidQuantity.value
            ? 'danger'
            : validation.value.warnings.length > 0
                ? 'warning'
                : 'success');
        const areDimensionsValid = computed(() => size.value.width > 0 && size.value.length > 0);
        const update = () => {
            if (!isValidQuantity.value)
                return;
            validate();
            updatePrice();
            checkStock();
        };
        const debouncedUpdated = debounce(update, 350);
        watch(size, debouncedUpdated, { deep: true });
        watch(quantity, debouncedUpdated);
        async function validate() {
            if (!product.value?.sku)
                return;
            loadingValidation.value = true;
            const v = await validateSheet(product.value.sku, size.value);
            const d = await sheetDelivery(product.value.sku, size.value);
            Object.assign(validation.value, v);
            Object.assign(delivery.value, d);
            loadingValidation.value = false;
        }
        async function updatePrice() {
            if (!product.value?.sku)
                return;
            loadingPrice.value = true;
            const newPricing = await getSheetPrice(product.value.sku, size.value, false, null, quantity.value);
            Object.assign(pricing.value, newPricing);
            loadingPrice.value = false;
        }
        async function checkStock() {
            if (!product.value?.sku)
                return;
            loadingStock.value = true;
            isInStock.value = await checkSheetStock(product.value.sku, size.value, quantity.value);
            loadingStock.value = false;
        }
        async function addToCartHandler() {
            if (!product.value?.sku)
                return;
            isAddingToCart.value = true;
            try {
                await addToCart({
                    sku: product.value.sku,
                    qty: quantity.value,
                    attributes: {
                        'Size': `${size.value.width}x${size.value.length}mm`,
                        'Delivery Available': delivery.value.isValid.toString()
                    }
                }, false);
            }
            finally {
                isAddingToCart.value = false;
            }
        }
        watch(() => pricing.value.base_unit_price, async () => {
            if (!product.value)
                return;
        });
        function onSizeTypeChange() {
            if (sizeType.value === 'standard') {
                size.value.width = 210;
                size.value.length = 297;
                size.value.name = 'A4';
                size.value.type = 'a';
                // ensure pricing/stock/validation refresh when switching back to standard
                debouncedUpdated();
            }
            else if (sizeType.value === 'bespoke') {
                switch (size.value.type) {
                    case 'inch':
                    case 'square':
                        displayUnit.value = 'inch';
                        break;
                    case 'cm':
                        displayUnit.value = 'cm';
                        break;
                    case 'a':
                        displayUnit.value = 'mm';
                        break;
                }
                delete size.value.name;
                delete size.value.type;
            }
        }
        function onStandardSizeChanged(newSize) {
            // adopt emitted standard size and trigger refresh
            Object.assign(size.value, newSize);
            sizeType.value = 'standard';
            debouncedUpdated();
        }
        onMounted(async () => {
            const { data } = await axios.get('accessories/productBySlug', { params: { slug: props.productSlug } });
            product.value = data;
            await updatePrice();
            await checkStock();
            loading.value = false;
        });
        vatMode.value = getVatMode();
        isAdmin().then(a => isAdminUser.value = a);
        return { __sfc: true, urlParams, axios, loading, loadingPrice, loadingValidation, loadingStock, isAddingToCart, props, isAdminUser, vatMode, product, size, quantity, isInStock, validation, delivery, sizeType, sizeTypeOptions, displayUnit, pricing, isValidQuantity, isAddToCartDisabled, buyButtonVariant, areDimensionsValid, update, debouncedUpdated, validate, updatePrice, checkStock, addToCartHandler, onSizeTypeChange, onStandardSizeChanged, ref, computed, watch, onMounted, debounce, BOverlay, BFormRadioGroup, BFormGroup, BFormInput, BButton, BAlert, UnitSelector, SizeInput, StandardSizeSelector, UpsellSheetProduct, ProductDiscontinued, DiscountsBreakdown, addToCart, validateSheet, sheetDelivery, checkSheetStock, getSheetPrice, getVatMode, isAdmin, useAxios };
    }
});
